import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Divider, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment, FormControlLabel, Switch,
  Avatar, Badge, Autocomplete
} from '@mui/material';
import {
  Hotel, Add, Edit, Delete, Search, Refresh, CheckCircle,
  Warning, ExitToApp, MeetingRoom, People, BarChart,
  Assignment, Report, Bed, FileDownload, Home, PersonOff
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const GENDER_COLOR   = { male:'info', female:'secondary', mixed:'default' };
const EXEAT_COLOR    = { pending:'warning', approved:'success', rejected:'error', departed:'primary', returned:'default', overdue:'error' };
const ATT_COLOR      = { present:'success', absent:'error', on_exeat:'primary', sick:'warning', other:'default' };
const ATT_OPTIONS    = ['present','absent','on_exeat','sick','other'];
const INCIDENT_TYPES = ['rule_violation','damage','missing','commendation','other'];
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const ptLabel = v => (v || '').replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase());

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    api.get('/boarding/stats', { params: { academic_year_id: selectedYear } })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>
      {loading ? <CircularProgress /> : stats && (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Boarders',    value: stats.occupancy?.total_boarders || 0,     color: '#0f2b4a' },
              { label: 'Active Houses',     value: stats.occupancy?.active_houses  || 0,     color: '#2e7d32' },
              { label: 'Exeats Pending',    value: stats.exeat_stats?.pending      || 0,     color: '#1565c0' },
              { label: 'Currently Out',     value: stats.exeat_stats?.out          || 0,     color: '#e65100' },
              { label: 'Overdue Returns',   value: stats.exeat_stats?.overdue      || 0,     color: '#c62828' },
            ].map(s => (
              <Grid item xs={6} sm key={s.label}>
                <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${s.color}` }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={10}>{s.label}</Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {stats.exeat_stats?.overdue > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>{stats.exeat_stats.overdue} student(s) have not returned by their expected return time.</strong> Please investigate immediately.
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Occupancy by house */}
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>🏠 House Occupancy</Typography>
                  {!stats.by_house?.length && <Typography fontSize={12} color="text.secondary">No houses configured yet</Typography>}
                  {stats.by_house?.map((h, i) => {
                    const pct = h.capacity > 0 ? Math.round((h.boarders / h.capacity) * 100) : 0;
                    return (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box>
                            <Typography fontSize={13} fontWeight={700}>{h.house_name}</Typography>
                            <Typography fontSize={11} color="text.secondary">
                              {h.house_master || 'No house master'} ·&nbsp;
                              <Chip label={ptLabel(h.gender)} size="small" color={GENDER_COLOR[h.gender]} sx={{ fontSize: 9, height: 16 }} />
                            </Typography>
                          </Box>
                          <Typography fontSize={12} fontWeight={700}>{h.boarders}/{h.capacity}</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.min(pct, 100)}
                          color={pct >= 95 ? 'error' : pct >= 80 ? 'warning' : 'success'}
                          sx={{ height: 8, borderRadius: 4 }} />
                        <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.3 }}>{pct}% occupied</Typography>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>

            {/* Recent exeats needing attention */}
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>🚪 Exeat Tracker</Typography>
                  {!stats.recent_exeats?.length && <Typography fontSize={12} color="success.main">No pending exeats</Typography>}
                  {stats.recent_exeats?.map((e, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6, borderBottom: '1px solid #f0f0f0' }}>
                      <Box>
                        <Typography fontSize={12} fontWeight={600}>{e.first_name} {e.last_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{e.house_name} · {e.destination || 'No destination'}</Typography>
                        <Typography fontSize={10} color="text.secondary">Out: {fmtD(e.departure_date)} → Return: {fmtD(e.return_date)}</Typography>
                      </Box>
                      <Chip label={ptLabel(e.status)} size="small" color={EXEAT_COLOR[e.status] || 'default'} sx={{ fontSize: 10, fontWeight: 700 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// ── Houses & Rooms Tab ─────────────────────────────────────────────────────────
function HousesTab({ academicYears, teachers }) {
  const { enqueueSnackbar } = useSnackbar();
  const [houses, setHouses]       = useState([]);
  const [rooms, setRooms]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selectedYear, setYear]   = useState('');
  const [selectedHouse, setHouseFilter] = useState('');
  const [houseDialog, setHouseDialog] = useState(false);
  const [roomDialog, setRoomDialog]   = useState(false);
  const [editingHouse, setEditHouse]  = useState(null);
  const [editingRoom, setEditRoom]    = useState(null);

  const BLANK_HOUSE = { name:'', gender:'male', house_master_id:'', capacity:0, description:'' };
  const BLANK_ROOM  = { house_id:'', room_number:'', capacity:4, room_type:'dormitory', floor:1, notes:'' };
  const [houseForm, setHouseForm] = useState(BLANK_HOUSE);
  const [roomForm, setRoomForm]   = useState(BLANK_ROOM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedYear) params.academic_year_id = selectedYear;
      if (selectedHouse) params.house_id = selectedHouse;
      const [hr, rr] = await Promise.all([
        api.get('/boarding/houses'),
        api.get('/boarding/rooms', { params })
      ]);
      if (hr.data.success) setHouses(hr.data.data);
      if (rr.data.success) setRooms(rr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, selectedHouse, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const saveHouse = async () => {
    try {
      if (editingHouse) { await api.put(`/boarding/houses/${editingHouse.id}`, { ...houseForm, is_active: 1 }); }
      else { await api.post('/boarding/houses', houseForm); }
      enqueueSnackbar(editingHouse ? 'House updated' : 'House created', { variant: 'success' });
      setHouseDialog(false); setEditHouse(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const saveRoom = async () => {
    try {
      if (editingRoom) { await api.put(`/boarding/rooms/${editingRoom.id}`, { ...roomForm, is_active: 1 }); }
      else { await api.post('/boarding/rooms', roomForm); }
      enqueueSnackbar(editingRoom ? 'Room updated' : 'Room created', { variant: 'success' });
      setRoomDialog(false); setEditRoom(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const filteredRooms = selectedHouse ? rooms.filter(r => r.house_id == selectedHouse) : rooms;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Filter by House" value={selectedHouse} onChange={e => setHouseFilter(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All Houses</MenuItem>
          {houses.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => { setEditHouse(null); setHouseForm({...BLANK_HOUSE}); setHouseDialog(true); }}>
          Add House
        </Button>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => { setEditRoom(null); setRoomForm({ ...BLANK_ROOM, house_id: selectedHouse || '' }); setRoomDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Add Room
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Grid container spacing={2}>
          {/* Houses */}
          {houses.filter(h => !selectedHouse || h.id == selectedHouse).map(house => {
            const houseRooms = rooms.filter(r => r.house_id === house.id);
            const totalCap   = houseRooms.reduce((s, r) => s + r.capacity, 0);
            const totalOcc   = houseRooms.reduce((s, r) => s + (r.occupied || 0), 0);
            return (
              <Grid item xs={12} key={house.id}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Hotel sx={{ color: '#0f2b4a', fontSize: 28 }} />
                        <Box>
                          <Typography fontWeight={700} fontSize={16}>{house.name}</Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Chip label={ptLabel(house.gender)} size="small" color={GENDER_COLOR[house.gender]} sx={{ fontSize: 10 }} />
                            <Typography fontSize={12} color="text.secondary">
                              House Master: {house.house_master_name || 'Not assigned'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={`${totalOcc}/${totalCap} occupied`} size="small"
                          color={totalOcc >= totalCap ? 'error' : totalOcc >= totalCap * 0.8 ? 'warning' : 'success'}
                          sx={{ fontWeight: 700 }} />
                        <IconButton size="small" onClick={() => { setEditHouse(house); setHouseForm({ name: house.name, gender: house.gender, house_master_id: house.house_master_id || '', capacity: house.capacity, description: house.description || '' }); setHouseDialog(true); }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>

                    {/* Room grid */}
                    <Grid container spacing={1.5}>
                      {houseRooms.length === 0 && (
                        <Grid item xs={12}><Typography fontSize={12} color="text.secondary">No rooms yet. Add rooms to this house.</Typography></Grid>
                      )}
                      {houseRooms.map(room => {
                        const pct = room.capacity > 0 ? Math.round(((room.occupied || 0) / room.capacity) * 100) : 0;
                        const full = (room.occupied || 0) >= room.capacity;
                        return (
                          <Grid item xs={6} sm={4} md={3} lg={2} key={room.id}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${full ? '#fca5a5' : '#e0e0e0'}`, bgcolor: full ? '#fff8f8' : 'white' }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                  <Typography fontWeight={700} fontSize={14}>Room {room.room_number}</Typography>
                                  <Typography fontSize={10} color="text.secondary">Floor {room.floor} · {ptLabel(room.room_type)}</Typography>
                                </Box>
                                <IconButton size="small" sx={{ p: 0.3 }} onClick={() => { setEditRoom(room); setRoomForm({ house_id: room.house_id, room_number: room.room_number, capacity: room.capacity, room_type: room.room_type, floor: room.floor, notes: room.notes || '' }); setRoomDialog(true); }}>
                                  <Edit sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Stack>
                              <Box sx={{ mt: 1 }}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  {Array.from({ length: room.capacity }).map((_, bi) => (
                                    <Bed key={bi} sx={{ fontSize: 14, color: bi < (room.occupied || 0) ? '#0f2b4a' : '#ddd' }} />
                                  ))}
                                </Stack>
                                <Typography fontSize={10} color={full ? 'error.main' : 'text.secondary'} fontWeight={full ? 700 : 400}>
                                  {room.occupied || 0}/{room.capacity} beds {full ? '(Full)' : ''}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {houses.length === 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                <Hotel sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No boarding houses configured. Click "Add House" to start.</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* House Dialog */}
      <Dialog open={houseDialog} onClose={() => setHouseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editingHouse ? 'Edit House' : 'Add Boarding House'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={8}><TextField fullWidth size="small" label="House Name *" value={houseForm.name} onChange={e => setHouseForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Swazi House, Nelson Mandela House" /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth size="small" label="Gender" value={houseForm.gender} onChange={e => setHouseForm(f => ({ ...f, gender: e.target.value }))}>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={8}>
              <TextField select fullWidth size="small" label="House Master / Mistress" value={houseForm.house_master_id} onChange={e => setHouseForm(f => ({ ...f, house_master_id: e.target.value }))}>
                <MenuItem value="">Not assigned</MenuItem>
                {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Capacity" value={houseForm.capacity} onChange={e => setHouseForm(f => ({ ...f, capacity: parseInt(e.target.value) || 0 }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description / Notes" value={houseForm.description} onChange={e => setHouseForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHouseDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveHouse} sx={{ bgcolor: '#0f2b4a' }}>{editingHouse ? 'Update' : 'Create House'}</Button>
        </DialogActions>
      </Dialog>

      {/* Room Dialog */}
      <Dialog open={roomDialog} onClose={() => setRoomDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingRoom ? 'Edit Room' : 'Add Room'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="House *" value={roomForm.house_id} onChange={e => setRoomForm(f => ({ ...f, house_id: e.target.value }))}>
                {houses.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Room Number *" value={roomForm.room_number} onChange={e => setRoomForm(f => ({ ...f, room_number: e.target.value }))} placeholder="e.g. 101, A1" /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Bed Capacity" value={roomForm.capacity} onChange={e => setRoomForm(f => ({ ...f, capacity: parseInt(e.target.value) || 1 }))} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Room Type" value={roomForm.room_type} onChange={e => setRoomForm(f => ({ ...f, room_type: e.target.value }))}>
                <MenuItem value="dormitory">Dormitory</MenuItem>
                <MenuItem value="semi_private">Semi-Private</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Floor" value={roomForm.floor} onChange={e => setRoomForm(f => ({ ...f, floor: parseInt(e.target.value) || 1 }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={roomForm.notes} onChange={e => setRoomForm(f => ({ ...f, notes: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRoom} sx={{ bgcolor: '#0f2b4a' }}>{editingRoom ? 'Update' : 'Add Room'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Allocations Tab ────────────────────────────────────────────────────────────
function AllocationsTab({ academicYears, students }) {
  const { enqueueSnackbar } = useSnackbar();
  const [allocations, setAllocations] = useState([]);
  const [houses, setHouses]           = useState([]);
  const [rooms, setRooms]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selectedYear, setYear]       = useState('');
  const [filterHouse, setHouseFilter] = useState('');
  const [dialog, setDialog]           = useState(false);
  const [editing, setEditing]         = useState(null);
  const [search, setSearch]           = useState('');
  const BLANK = { student_id:'', academic_year_id:'', house_id:'', room_id:'', bed_number:'', check_in_date:'', notes:'' };
  const [form, setForm] = useState(BLANK);

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const params = { academic_year_id: selectedYear, status: 'active' };
      if (filterHouse) params.house_id = filterHouse;
      const [ar, hr, rr] = await Promise.all([
        api.get('/boarding/allocations', { params }),
        api.get('/boarding/houses'),
        api.get('/boarding/rooms', { params: { academic_year_id: selectedYear } })
      ]);
      if (ar.data.success) setAllocations(ar.data.data);
      if (hr.data.success) setHouses(hr.data.data);
      if (rr.data.success) setRooms(rr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, filterHouse, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async () => {
    try {
      if (editing) { await api.put(`/boarding/allocations/${editing.id}`, form); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/boarding/allocations', form); enqueueSnackbar('Student allocated', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this allocation?')) return;
    try { await api.delete(`/boarding/allocations/${id}`); enqueueSnackbar('Removed', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const filteredRooms = rooms.filter(r => !form.house_id || r.house_id == form.house_id);
  const filtered = allocations.filter(a => !search ||
    `${a.first_name} ${a.last_name} ${a.admission_number}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="House" value={filterHouse} onChange={e => setHouseFilter(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All Houses</MenuItem>
          {houses.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
        </TextField>
        <TextField size="small" placeholder="Search student…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 200 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <Box sx={{ flexGrow: 1 }} />
        <Chip label={`${filtered.length} boarders`} size="small" />
        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('boarders', ['Student','Admission','Gender','House','Room','Bed','Check-in'],
            filtered.map(a => [`${a.first_name} ${a.last_name}`, a.admission_number, a.gender, a.house_name, a.room_number, a.bed_number||'', fmtD(a.check_in_date)]))
        }>Export</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({ ...BLANK, academic_year_id: selectedYear }); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Allocate Student
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Student','Gender','House','Room','Bed','Check-in','Status','Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No allocations found</TableCell></TableRow>}
                {filtered.map((a, i) => (
                  <TableRow key={a.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <TableCell>
                      <Typography fontSize={12} fontWeight={600}>{a.last_name}, {a.first_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{a.admission_number}</Typography>
                    </TableCell>
                    <TableCell><Chip label={ptLabel(a.gender)} size="small" color={a.gender === 'male' ? 'info' : 'secondary'} sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{a.house_name}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>Room {a.room_number}</TableCell>
                    <TableCell align="center" sx={{ fontSize: 12 }}>{a.bed_number || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtD(a.check_in_date)}</TableCell>
                    <TableCell><Chip label={ptLabel(a.status)} size="small" color={a.status === 'active' ? 'success' : 'default'} sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Edit allocation">
                          <IconButton size="small" onClick={() => { setEditing(a); setForm({ student_id: a.student_id, academic_year_id: a.academic_year_id, house_id: a.house_id, room_id: a.room_id, bed_number: a.bed_number || '', check_in_date: a.check_in_date?.split('T')[0] || '', check_out_date: a.check_out_date?.split('T')[0] || '', status: a.status, notes: a.notes || '' }); setDialog(true); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => remove(a.id)}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Allocation Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit Allocation' : 'Allocate Student to Room'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {!editing && (
              <Grid item xs={12}>
                <Autocomplete size="small" options={students}
                  getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                  onChange={(_, v) => setForm(f => ({ ...f, student_id: v?.id || '' }))}
                  renderInput={p => <TextField {...p} label="Student *" />} />
              </Grid>
            )}
            {!editing && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id} onChange={e => setForm(f => ({ ...f, academic_year_id: e.target.value }))}>
                  {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="House *" value={form.house_id} onChange={e => setForm(f => ({ ...f, house_id: e.target.value, room_id: '' }))}>
                <MenuItem value="">Select house…</MenuItem>
                {houses.map(h => <MenuItem key={h.id} value={h.id}>{h.name} ({ptLabel(h.gender)})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Room *" value={form.room_id} onChange={e => setForm(f => ({ ...f, room_id: e.target.value }))}>
                <MenuItem value="">Select room…</MenuItem>
                {filteredRooms.map(r => <MenuItem key={r.id} value={r.id} disabled={(r.occupied || 0) >= r.capacity}>
                  Room {r.room_number} — {r.occupied || 0}/{r.capacity} {(r.occupied || 0) >= r.capacity ? '(Full)' : ''}
                </MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Bed Number" value={form.bed_number} onChange={e => setForm(f => ({ ...f, bed_number: e.target.value }))} /></Grid>
            <Grid item xs={4}><TextField fullWidth size="small" type="date" label="Check-in Date" value={form.check_in_date} onChange={e => setForm(f => ({ ...f, check_in_date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
            {editing && (
              <>
                <Grid item xs={4}><TextField fullWidth size="small" type="date" label="Check-out Date" value={form.check_out_date || ''} onChange={e => setForm(f => ({ ...f, check_out_date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12}>
                  <TextField select fullWidth size="small" label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {['active','checked_out','suspended','transferred'].map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={editing ? 8 : 12}><TextField fullWidth size="small" label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Allocate'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Exeats Tab ─────────────────────────────────────────────────────────────────
function ExeatsTab({ academicYears, students }) {
  const { enqueueSnackbar } = useSnackbar();
  const [exeats, setExeats]         = useState([]);
  const [houses, setHouses]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selectedYear, setYear]     = useState('');
  const [filterStatus, setStatus]   = useState('');
  const [filterHouse, setHouseFilter] = useState('');
  const [dialog, setDialog]         = useState(false);
  const [editing, setEditing]       = useState(null);
  const BLANK = { student_id:'', academic_year_id:'', departure_date:'', departure_time:'14:00', return_date:'', return_time:'18:00', destination:'', contact_person:'', contact_phone:'', reason:'' };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const params = { academic_year_id: selectedYear };
      if (filterStatus) params.status   = filterStatus;
      if (filterHouse)  params.house_id = filterHouse;
      const [er, hr] = await Promise.all([
        api.get('/boarding/exeats', { params }),
        api.get('/boarding/houses')
      ]);
      if (er.data.success) setExeats(er.data.data);
      if (hr.data.success) setHouses(hr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, filterStatus, filterHouse, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async () => {
    try {
      if (editing) { await api.put(`/boarding/exeats/${editing.id}`, form); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/boarding/exeats', form); enqueueSnackbar('Exeat submitted', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const quickUpdate = async (id, status) => {
    try {
      const extra = status === 'departed' ? { actual_departure: new Date().toISOString() }
        : status === 'returned' ? { actual_return: new Date().toISOString() } : {};
      await api.put(`/boarding/exeats/${id}`, { status, ...extra });
      enqueueSnackbar(`Marked as ${status}`, { variant: 'success' }); load();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this exeat?')) return;
    try { await api.delete(`/boarding/exeats/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setStatus(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">All</MenuItem>
          {['pending','approved','rejected','departed','returned','overdue'].map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="House" value={filterHouse} onChange={e => setHouseFilter(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">All Houses</MenuItem>
          {houses.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
        </TextField>
        <Chip label={`${exeats.filter(e => e.status === 'overdue').length} overdue`} size="small" color="error" />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({ ...BLANK, academic_year_id: selectedYear }); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          New Exeat
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : exeats.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <ExitToApp sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No exeat passes found for the selected filters.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {exeats.map(e => (
            <Grid item xs={12} sm={6} md={4} key={e.id}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: e.status === 'overdue' ? '2px solid #c62828' : e.status === 'pending' ? '1px solid #fbbf24' : undefined }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                    <Box>
                      <Typography fontWeight={700} fontSize={13}>{e.last_name}, {e.first_name}</Typography>
                      <Typography fontSize={11} color="text.secondary">{e.admission_number} · {e.house_name || '—'}</Typography>
                    </Box>
                    <Chip label={ptLabel(e.status)} size="small" color={EXEAT_COLOR[e.status] || 'default'} sx={{ fontSize: 10, fontWeight: 700 }} />
                  </Stack>

                  <Divider sx={{ my: 0.8 }} />
                  <Grid container spacing={0.5}>
                    <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Departure</Typography><Typography fontSize={12} fontWeight={600}>{fmtD(e.departure_date)}</Typography><Typography fontSize={10} color="text.secondary">{e.departure_time?.slice(0,5)}</Typography></Grid>
                    <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Return</Typography><Typography fontSize={12} fontWeight={600} color={e.status === 'overdue' ? 'error.main' : 'inherit'}>{fmtD(e.return_date)}</Typography><Typography fontSize={10} color="text.secondary">{e.return_time?.slice(0,5)}</Typography></Grid>
                  </Grid>

                  {e.destination && <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5 }}>📍 {e.destination}</Typography>}
                  {e.contact_person && <Typography fontSize={11} color="text.secondary">👤 {e.contact_person} {e.contact_phone ? `· ${e.contact_phone}` : ''}</Typography>}

                  <Divider sx={{ my: 0.8 }} />
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {e.status === 'pending' && (
                      <>
                        <Button size="small" variant="contained" color="success" onClick={() => quickUpdate(e.id, 'approved')} sx={{ fontSize: 10, py: 0.3 }}>Approve</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => quickUpdate(e.id, 'rejected')} sx={{ fontSize: 10, py: 0.3 }}>Reject</Button>
                      </>
                    )}
                    {e.status === 'approved' && (
                      <Button size="small" variant="outlined" color="primary" onClick={() => quickUpdate(e.id, 'departed')} sx={{ fontSize: 10, py: 0.3 }}>Mark Departed</Button>
                    )}
                    {e.status === 'departed' && (
                      <Button size="small" variant="contained" color="success" onClick={() => quickUpdate(e.id, 'returned')} sx={{ fontSize: 10, py: 0.3 }}>Mark Returned</Button>
                    )}
                    {e.status === 'overdue' && (
                      <Button size="small" variant="contained" color="success" onClick={() => quickUpdate(e.id, 'returned')} sx={{ fontSize: 10, py: 0.3 }}>Mark Returned</Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton size="small" onClick={() => { setEditing(e); setForm({ student_id: e.student_id, academic_year_id: e.academic_year_id, departure_date: e.departure_date?.split('T')[0]||'', departure_time: e.departure_time?.slice(0,5)||'14:00', return_date: e.return_date?.split('T')[0]||'', return_time: e.return_time?.slice(0,5)||'18:00', destination: e.destination||'', contact_person: e.contact_person||'', contact_phone: e.contact_phone||'', reason: e.reason||'', status: e.status }); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => del(e.id)}><Delete fontSize="small" /></IconButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Exeat Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit Exeat' : 'Issue Exeat Pass'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {!editing && (
              <>
                <Grid item xs={12}>
                  <Autocomplete size="small" options={students}
                    getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                    onChange={(_, v) => setForm(f => ({ ...f, student_id: v?.id || '' }))}
                    renderInput={p => <TextField {...p} label="Student *" />} />
                </Grid>
                <Grid item xs={12}>
                  <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id} onChange={setF('academic_year_id')}>
                    {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
                  </TextField>
                </Grid>
              </>
            )}
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Departure Date *" value={form.departure_date} onChange={setF('departure_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="time" label="Departure Time" value={form.departure_time} onChange={setF('departure_time')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Return Date *" value={form.return_date} onChange={setF('return_date')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="time" label="Return Time" value={form.return_time} onChange={setF('return_time')} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Destination *" value={form.destination} onChange={setF('destination')} placeholder="e.g. Home — Mbabane" /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Contact Person" value={form.contact_person} onChange={setF('contact_person')} placeholder="Parent/Guardian name" /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Contact Phone" value={form.contact_phone} onChange={setF('contact_phone')} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Reason" value={form.reason} onChange={setF('reason')} multiline rows={2} /></Grid>
            {editing && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Status" value={form.status} onChange={setF('status')}>
                  {['pending','approved','rejected','departed','returned','overdue'].map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
                </TextField>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Submit Exeat'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Roll Call Tab ──────────────────────────────────────────────────────────────
function RollCallTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [houses, setHouses]         = useState([]);
  const [boarders, setBoarders]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [selectedYear, setYear]     = useState('');
  const [selectedHouse, setHouse]   = useState('');
  const [selectedDate, setDate]     = useState(new Date().toISOString().split('T')[0]);
  const [selectedSession, setSession] = useState('evening');
  const [statuses, setStatuses]     = useState({});
  const [notes, setNotes]           = useState({});
  const [loaded, setLoaded]         = useState(false);

  useEffect(() => { api.get('/boarding/houses').then(r => { if (r.data.success) setHouses(r.data.data); }); }, []);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const loadRoll = async () => {
    if (!selectedHouse || !selectedDate || !selectedYear) {
      enqueueSnackbar('Select house, date and year', { variant: 'warning' }); return;
    }
    setLoading(true);
    try {
      const r = await api.get('/boarding/roll-call', { params: { house_id: selectedHouse, date: selectedDate, session: selectedSession, academic_year_id: selectedYear } });
      if (r.data.success) {
        setBoarders(r.data.data);
        const s = {}, n = {};
        r.data.data.forEach(b => {
          s[b.student_id] = b.att_status || 'present';
          n[b.student_id] = b.att_notes || '';
        });
        setStatuses(s); setNotes(n); setLoaded(true);
      }
    } catch { enqueueSnackbar('Failed to load roll call', { variant: 'error' }); }
    finally { setLoading(false); }
  };

  const saveRoll = async () => {
    setSaving(true);
    try {
      const entries = boarders.map(b => ({ student_id: b.student_id, status: statuses[b.student_id] || 'present', notes: notes[b.student_id] || '' }));
      await api.post('/boarding/roll-call', { house_id: selectedHouse, academic_year_id: selectedYear, date: selectedDate, session: selectedSession, entries });
      enqueueSnackbar(`Roll call saved — ${entries.length} records`, { variant: 'success' });
    } catch { enqueueSnackbar('Error saving roll call', { variant: 'error' }); }
    finally { setSaving(false); }
  };

  const grouped = boarders.reduce((g, b) => {
    const k = b.room_number;
    if (!g[k]) g[k] = [];
    g[k].push(b); return g;
  }, {});

  const presentCount = Object.values(statuses).filter(s => s === 'present').length;
  const absentCount  = Object.values(statuses).filter(s => s === 'absent').length;

  return (
    <Box>
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)}>
              {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select fullWidth size="small" label="House" value={selectedHouse} onChange={e => setHouse(e.target.value)}>
              <MenuItem value="">Select…</MenuItem>
              {houses.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select fullWidth size="small" label="Session" value={selectedSession} onChange={e => setSession(e.target.value)}>
              <MenuItem value="morning">Morning</MenuItem>
              <MenuItem value="evening">Evening</MenuItem>
              <MenuItem value="lights_out">Lights Out</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" type="date" label="Date" value={selectedDate} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Button variant="contained" onClick={loadRoll} disabled={loading} sx={{ bgcolor: '#0f2b4a', mr: 1 }}>Load Roll</Button>
            {loaded && <Button variant="contained" color="success" onClick={saveRoll} disabled={saving}>{saving ? 'Saving…' : 'Save Roll'}</Button>}
          </Grid>
        </Grid>
      </Card>

      {loaded && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip label={`${boarders.length} total`} size="small" />
            <Chip label={`${presentCount} present`} size="small" color="success" />
            <Chip label={`${absentCount} absent`} size="small" color="error" />
            <Chip label={`${Object.values(statuses).filter(s => s === 'on_exeat').length} on exeat`} size="small" color="primary" />
          </Stack>

          {Object.entries(grouped).sort().map(([room, roomBoarders]) => (
            <Card key={room} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>
                <Typography fontWeight={700} fontSize={14} sx={{ mb: 1.5 }}>Room {room}</Typography>
                <Grid container spacing={1}>
                  {roomBoarders.map(b => (
                    <Grid item xs={12} sm={6} md={4} key={b.student_id}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2,
                        border: `2px solid ${statuses[b.student_id] === 'present' ? '#2e7d32' : statuses[b.student_id] === 'absent' ? '#c62828' : statuses[b.student_id] === 'on_exeat' ? '#1565c0' : '#e65100'}` }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                          <Box>
                            <Typography fontSize={13} fontWeight={700}>{b.last_name}, {b.first_name}</Typography>
                            <Typography fontSize={10} color="text.secondary">{b.admission_number}{b.bed_number ? ` · Bed ${b.bed_number}` : ''}</Typography>
                          </Box>
                          <Chip label={ptLabel(statuses[b.student_id] || 'present')} size="small"
                            color={ATT_COLOR[statuses[b.student_id]] || 'default'} sx={{ fontSize: 10, fontWeight: 700 }} />
                        </Stack>
                        <TextField select fullWidth size="small" value={statuses[b.student_id] || 'present'}
                          onChange={e => setStatuses(s => ({ ...s, [b.student_id]: e.target.value }))}
                          sx={{ mb: 0.5 }}>
                          {ATT_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{ptLabel(opt)}</MenuItem>)}
                        </TextField>
                        {statuses[b.student_id] !== 'present' && (
                          <TextField fullWidth size="small" placeholder="Notes…" value={notes[b.student_id] || ''}
                            onChange={e => setNotes(n => ({ ...n, [b.student_id]: e.target.value }))} />
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ))}
        </>
      )}

      {!loaded && !loading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Assignment sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Select a house, date and session, then click "Load Roll" to take attendance.</Typography>
        </Paper>
      )}
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function BoardingHouse() {
  const [tab, setTab]           = useState(0);
  const [academicYears, setAY]  = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    import('../services/api').then(({ studentsAPI, teachersAPI }) => {
      studentsAPI.getAll({ limit: 2000, status: 'active' }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
      teachersAPI.getAll({ limit: 500 }).then(r => { if (r.data.success) setTeachers(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Hotel color="primary" /> Boarding House
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Houses & rooms · Allocations · Exeat passes · Roll call · Incidents
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}    label="Overview"     iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Hotel fontSize="small" />}       label="Houses"       iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<People fontSize="small" />}      label="Boarders"     iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<ExitToApp fontSize="small" />}   label="Exeats"       iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Assignment fontSize="small" />}  label="Roll Call"    iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><HousesTab academicYears={academicYears} teachers={teachers} /></TabPanel>
      <TabPanel value={tab} index={2}><AllocationsTab academicYears={academicYears} students={students} /></TabPanel>
      <TabPanel value={tab} index={3}><ExeatsTab academicYears={academicYears} students={students} /></TabPanel>
      <TabPanel value={tab} index={4}><RollCallTab academicYears={academicYears} /></TabPanel>
    </Box>
  );
}
